import ReadMoreText from "Components/Common/ReadMoreText";
import Icon from "Components/Icons/Icon";
import {
  Card,
  CardBody,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import { InvoiceDetailsPayload } from "types/invoice.api";

interface InvoiceDescriptionProps {
  invoice: InvoiceDetailsPayload;
}

const InvoiceDescription = ({ invoice }: InvoiceDescriptionProps) => {
  const downloadFile = async (url: string, filename?: string) => {
    // Derive a filename from the URL if not provided
    const resolvedFilename =
      filename || url.split("/").pop()?.split("?")[0] || "document.pdf";

    try {
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error(`Failed to fetch PDF: ${response.status}`);
      }

      const blob = await response.blob();
      const blobUrl = URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = resolvedFilename; // now works because it's a same-origin blob URL
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Release the blob URL after a short delay
      setTimeout(() => URL.revokeObjectURL(blobUrl), 10_000);
    } catch (error) {
      console.warn("Blob download failed, falling back to new tab:", error);
      // Fallback: open in new tab so the user can manually save
      window.open(url, "_blank", "noopener,noreferrer");
    }
  };

  const openInNewTab = (url: string) => {
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <Card id="InvoiceDescription" className="br-10 bg-white">
      <CardBody>
        <div className="fw-medium fs-16 text-black2D mb-3">
          Project / Services Notes
        </div>
        <ReadMoreText text={invoice.message} />
        <div className="dashed" />
        <div className="fw-medium fs-16 text-black2D mb-3">
          Notes (Not visible on invoice)
        </div>
        <ReadMoreText text={invoice.notes || ""} />
        <div className="dashed" />
        <div className="fw-medium fs-16 text-black2D ">Attachments</div>
        <div className="mt-2 row g-2">
          {invoice.attachments.map((item) => (
            <div key={item.id} className="col-12 col-sm-6 col-md-4 mb-1">
              <div className="d-flex p-2 align-items-center  justify-content-between br-7 dashed m-0">
                <div className="d-flex align-items-center gap-2">
                  <div
                    className="bg-greyF5"
                    style={{
                      width: 32,
                      height: 32,
                      borderRadius: 3,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <Icon name="Link" />
                  </div>
                  <div className="fw-normal fs-14 text-grey89">
                    {item.type} Asset.{item.type}
                  </div>
                </div>
                <UncontrolledDropdown>
                  <DropdownToggle className="btn btn-sm dropdown" tag="button">
                    <i className="ri-more-fill align-middle"></i>
                  </DropdownToggle>

                  <DropdownMenu className="dropdown-menu-end">
                    <DropdownItem
                      onClick={() => {
                        openInNewTab(item.url);
                      }}
                    >
                      <i className="ri-eye-fill me-2 text-muted"></i> View
                    </DropdownItem>

                    <DropdownItem
                      onClick={() => {
                        downloadFile(item.url, `${item.id}.${item.type}`);
                      }}
                    >
                      <svg
                        className="mr-2"
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M12 4V16"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M6 12L12 18L18 12"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                        <path
                          d="M4 20H20"
                          stroke="currentColor"
                          stroke-width="2"
                          stroke-linecap="round"
                          stroke-linejoin="round"
                        />
                      </svg>{" "}
                      Download
                    </DropdownItem>
                  </DropdownMenu>
                </UncontrolledDropdown>
              </div>
            </div>
          ))}
        </div>
      </CardBody>
    </Card>
  );
};

export default InvoiceDescription;
